import { memo } from 'react'
import { Languages, Wand2 } from 'lucide-react'
import { cn } from '@/internationalization/cn'
import { formatTimecode } from '@/internationalization/format'
import { useT } from '@/internationalization/i18n'
import { LANGUAGE_LABEL } from '@/internationalization/constants'
import { SpeakerChip } from '@/components/domain'
import { Tooltip } from '@/components/ui'
import { isUnattributed } from '@/internationalization/speaker-color'
import type { TranscriptSegment } from '@/types'

/**
 * One transcript segment.
 *
 * Three honesty rules, all of which exist because a transcript that hides its
 * own uncertainty gets quoted as fact:
 *  - An unnamed voice is labelled "Unnamed", never given a colour or a guess.
 *  - Low ASR confidence dims the text and says so on hover, rather than
 *    rendering a shaky decode in the same weight as a clean one.
 *  - A non-primary language is marked, because a Hindi line silently sitting in
 *    an English transcript is how a mistranslation goes unnoticed.
 *
 * MEMOISED, because the playhead now drives this list. The player publishes
 * `currentTime` four times a second while anything is playing, so every
 * context consumer re-renders at 4 Hz -- and `TranscriptPanel` maps the full
 * segment list, which real meetings push into the thousands, each line
 * rendering a `SpeakerChip` and up to three portalled tooltips. `onSeek` is
 * referentially stable (`playFrom` is a `useCallback`), so this reduces a
 * full-list reconcile per tick to the two lines whose `isActive` changed.
 *
 * The same rules carry into the cleaned view (`clean`), which adds a fourth: a
 * line a model rewrote is MARKED as rewritten and its original is one hover
 * away. A repaired transcript that looks identical to a recorded one invites
 * being quoted as what somebody said, and on these meetings the commonest
 * repair is a person's NAME.
 */
export const TranscriptLine = memo(function TranscriptLine({
  segment,
  isActive,
  isMatch,
  onSeek,
  clean = false,
}: {
  segment: TranscriptSegment
  isActive: boolean
  /** Highlighted as a search hit. */
  isMatch?: boolean
  onSeek: (seconds: number) => void
  /** Render the repaired text where one exists. */
  clean?: boolean
}) {
  const t = useT()
  const lowConfidence = segment.confidence !== null && segment.confidence < 0.6
  const unknown = isUnattributed(segment.speakerName)
  // `textClean` is null for a line the repair left alone, so this is false for
  // most lines even on a meeting that was cleaned.
  const repaired = clean && segment.textClean !== null
  const body = repaired ? segment.textClean! : segment.text

  return (
    <div
      id={`segment-${segment.id}`}
      className={cn(
        'group flex gap-3 border-l-2 px-3 py-2.5 transition-colors',
        isActive ? 'border-brand bg-brand-subtle/40' : 'border-transparent hover:bg-surface-muted',
        isMatch && !isActive && 'bg-warning-subtle/50',
      )}
    >
      <button
        type="button"
        onClick={() => onSeek(segment.startSeconds)}
        className="mt-0.5 shrink-0 font-mono text-xs tabular-nums text-fg-subtle transition-colors
          hover:text-brand hover:underline"
        aria-label={t('transcript.line.playFrom', { time: formatTimecode(segment.startSeconds) })}
      >
        {formatTimecode(segment.startSeconds)}
      </button>

      <div className="min-w-0 flex-1">
        <div className="mb-0.5 flex flex-wrap items-center gap-2">
          <SpeakerChip name={segment.speakerName} label={segment.speakerLabel} />
          {segment.language && segment.language !== 'en-IN' && (
            <Tooltip content={LANGUAGE_LABEL[segment.language]}>
              <span className="inline-flex items-center gap-1 text-2xs font-medium text-fg-subtle">
                <Languages aria-hidden className="h-3 w-3" />
                {segment.language}
              </span>
            </Tooltip>
          )}
          {lowConfidence && (
            <Tooltip content={t('transcript.line.lowConfidenceTip')}>
              <span className="text-2xs font-medium text-warning">{t('transcript.line.lowConfidence')}</span>
            </Tooltip>
          )}
          {repaired && (
            <Tooltip content={t('transcript.line.originallyTranscribedAs', { text: segment.text })}>
              <span
                className="inline-flex items-center gap-1 text-2xs font-medium text-brand"
                data-testid="repaired-marker"
              >
                <Wand2 aria-hidden className="h-3 w-3" />
                {t('transcript.line.repaired')}
              </span>
            </Tooltip>
          )}
        </div>
        <p
          className={cn(
            'text-base leading-relaxed text-fg',
            lowConfidence && 'text-fg-muted',
            unknown && 'text-fg-muted',
          )}
        >
          {body}
        </p>
        {repaired && (
          /* The original, not a diff. A word-level diff reads as authoritative
             about WHICH word changed, and the repair returns a whole rewritten
             line -- so showing both in full is the honest rendering, and it is
             what lets a reader disagree with the model. */
          <p className="mt-1 text-sm leading-relaxed text-fg-subtle line-through decoration-fg-subtle/40">
            {segment.text}
          </p>
        )}
      </div>
    </div>
  )
})
